// Urvalet när byrån ändrar en offert (ADR 0069): vilka tjänster som ingår,
// som tillval eller inte alls, mängderna och drivarna. Priset räknar
// prismotorn i API:t, aldrig kontot.
import type { components } from '../api/schema.js';
import type { ServiceCatalog } from '../katalog/sammanfattning.js';
import type { LegalForm } from '../katalog/texter.js';

export type Urval = components['schemas']['Selection'];
export type Val = 'ingar' | 'tillval' | 'nej';

/** Motiveringen när byrån själv lägger till en tjänst. */
export const BYRANS_VAL = 'Vald av byrån';

export const tomtUrval = (legalForm: LegalForm): Urval => ({
  legalForm,
  services: [],
  optionalServices: [],
  drivers: {},
});

export function valAv(urval: Urval, serviceId: string): Val {
  if (urval.services.some((s) => s.serviceId === serviceId)) return 'ingar';
  if (urval.optionalServices.some((s) => s.serviceId === serviceId)) return 'tillval';
  return 'nej';
}

const vald = (urval: Urval, serviceId: string) =>
  [...urval.services, ...urval.optionalServices].find((s) => s.serviceId === serviceId);

/** Flyttar tjänsten. Motiveringen och mängden följer med. */
export function andraVal(
  urval: Urval,
  katalog: ServiceCatalog,
  serviceId: string,
  val: Val,
): Urval {
  const tjanst = katalog.services.find((s) => s.id === serviceId);
  const nu = vald(urval, serviceId);
  const post = nu ?? {
    serviceId,
    reason: BYRANS_VAL,
    ...(tjanst?.pricing.type === 'perUnit' ? { quantity: 1 } : {}),
  };
  const utan = (lista: Urval['services']) => lista.filter((s) => s.serviceId !== serviceId);
  return {
    ...urval,
    services: val === 'ingar' ? [...utan(urval.services), post] : utan(urval.services),
    optionalServices:
      val === 'tillval' ? [...utan(urval.optionalServices), post] : utan(urval.optionalServices),
  };
}

export function andraAntal(urval: Urval, serviceId: string, antal: number): Urval {
  const satt = (lista: Urval['services']) =>
    lista.map((s) => (s.serviceId === serviceId ? { ...s, quantity: antal } : s));
  return {
    ...urval,
    services: satt(urval.services),
    optionalServices: satt(urval.optionalServices),
  };
}

/** Drivarna som tjänsterna i urvalet behöver, till exempel transactionsPerMonth. */
export function drivareFor(katalog: ServiceCatalog, urval: Urval): string[] {
  const idn = new Set([...urval.services, ...urval.optionalServices].map((s) => s.serviceId));
  const kodar = katalog.services
    .filter((s) => idn.has(s.id))
    .flatMap((s) => {
      const p = s.pricing;
      if (p.type === 'tiered') return [p.driver];
      if (p.type === 'hourly') return p.timeNorms.flatMap((n) => ('driver' in n ? [n.driver] : []));
      return [];
    });
  return [...new Set(kodar)];
}

/** Byrån anger en siffra. Den ersätter AI:s uppskattning med ett exakt värde. */
export function andraDrivare(urval: Urval, kod: string, varde: number): Urval {
  return {
    ...urval,
    drivers: {
      ...urval.drivers,
      [kod]: { value: varde, low: varde, high: varde, source: 'stated' },
    },
  };
}

/** Drivarens värde i fältet: "100", eller "85–115" när AI har uppskattat ett intervall. */
export function drivareText(urval: Urval, kod: string): string {
  const d = urval.drivers[kod];
  if (!d) return '';
  return d.low === d.high ? String(d.value) : `${d.low}–${d.high}`;
}
